<template>
  <!--
    The six containers are the live regions, not the toasts.

    `ToastItem` carries its own `aria-live`, but it is the node being *inserted*:
    a screen reader registers a live region when it is already in the tree, so an
    element that arrives carrying the attribute is announced inconsistently — and
    a toast nobody hears is the whole point of a toast lost. These six wrappers
    are mounted for the life of the app, so they are what the reader watches.
    `aria-atomic="false"` keeps it to the toast that just arrived instead of
    re-reading the whole stack. Errors keep the assertive region `ToastItem`
    declares: the innermost live region governs its own subtree.
  -->
  <Teleport to="body">
    <!-- Top-right -->
    <div
      class="toast-container toast-container--top-right"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
    >
      <TransitionGroup name="toast-slide-right">
        <ToastItem
          v-for="toast in visibleToasts['top-right']"
          :key="toast.id"
          :toast="toast"
          class="mb-2"
          @close="handleClose"
        />
      </TransitionGroup>
    </div>

    <!-- Top-left -->
    <div
      class="toast-container toast-container--top-left"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
    >
      <TransitionGroup name="toast-slide-left">
        <ToastItem
          v-for="toast in visibleToasts['top-left']"
          :key="toast.id"
          :toast="toast"
          class="mb-2"
          @close="handleClose"
        />
      </TransitionGroup>
    </div>

    <!-- Top-center -->
    <div
      class="toast-container toast-container--top-center"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
    >
      <TransitionGroup name="toast-slide-down">
        <ToastItem
          v-for="toast in visibleToasts['top-center']"
          :key="toast.id"
          :toast="toast"
          class="mb-2"
          @close="handleClose"
        />
      </TransitionGroup>
    </div>

    <!-- Bottom-right -->
    <div
      class="toast-container toast-container--bottom-right"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
    >
      <TransitionGroup name="toast-slide-right">
        <ToastItem
          v-for="toast in visibleToasts['bottom-right']"
          :key="toast.id"
          :toast="toast"
          class="mb-2"
          @close="handleClose"
        />
      </TransitionGroup>
    </div>

    <!-- Bottom-left -->
    <div
      class="toast-container toast-container--bottom-left"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
    >
      <TransitionGroup name="toast-slide-left">
        <ToastItem
          v-for="toast in visibleToasts['bottom-left']"
          :key="toast.id"
          :toast="toast"
          class="mb-2"
          @close="handleClose"
        />
      </TransitionGroup>
    </div>

    <!-- Bottom-center -->
    <div
      class="toast-container toast-container--bottom-center"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
    >
      <TransitionGroup name="toast-slide-up">
        <ToastItem
          v-for="toast in visibleToasts['bottom-center']"
          :key="toast.id"
          :toast="toast"
          class="mb-2"
          @close="handleClose"
        />
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useToastStore } from '../../stores/useToastStore'
import ToastItem from './ToastItem.vue'

const store = useToastStore()

const visibleToasts = computed(() => store.visibleToasts)

function handleClose(id: string) {
  store.remove(id)
}
</script>

<style scoped lang="scss">
@use '@/styles/design-tokens' as *;

// The bottom ones stand on `--app-bottom-inset` (docs/plans/mobile-navigation.md, MN4): on a
// phone they clear the bottom bar and the mini player, and above it the inset is 0.
.toast-container {
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  &--top-right {
    top: 24px;
    right: 24px;
    align-items: flex-end;
  }

  &--top-left {
    top: 24px;
    left: 24px;
    align-items: flex-start;
  }

  &--top-center {
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
  }

  &--bottom-right {
    bottom: calc(var(--app-bottom-inset) + 24px);
    right: 24px;
    align-items: flex-end;
    flex-direction: column-reverse;
  }

  &--bottom-left {
    bottom: calc(var(--app-bottom-inset) + 24px);
    left: 24px;
    align-items: flex-start;
    flex-direction: column-reverse;
  }

  &--bottom-center {
    bottom: calc(var(--app-bottom-inset) + 24px);
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
    flex-direction: column-reverse;
  }

  @include phone {
    &--top-right,
    &--top-left,
    &--top-center {
      // `viewport-fit=cover` lets the page run under the notch; the toasts do not.
      top: calc(env(safe-area-inset-top, 0px) + 16px);
      right: 16px;
      left: 16px;
      transform: none;
    }

    &--bottom-right,
    &--bottom-left,
    &--bottom-center {
      bottom: calc(var(--app-bottom-inset) + 16px);
      right: 16px;
      left: 16px;
      transform: none;
    }
  }
}

// Slide transitions
.toast-slide-right-enter-active,
.toast-slide-right-leave-active,
.toast-slide-left-enter-active,
.toast-slide-left-leave-active,
.toast-slide-down-enter-active,
.toast-slide-down-leave-active,
.toast-slide-up-enter-active,
.toast-slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-slide-right-enter-from {
  opacity: 0;
  transform: translateX(100px);
}

.toast-slide-right-leave-to {
  opacity: 0;
  transform: translateX(100px);
}

.toast-slide-left-enter-from {
  opacity: 0;
  transform: translateX(-100px);
}

.toast-slide-left-leave-to {
  opacity: 0;
  transform: translateX(-100px);
}

.toast-slide-down-enter-from {
  opacity: 0;
  transform: translateY(-24px);
}

.toast-slide-down-leave-to {
  opacity: 0;
  transform: translateY(-24px);
}

.toast-slide-up-enter-from {
  opacity: 0;
  transform: translateY(24px);
}

.toast-slide-up-leave-to {
  opacity: 0;
  transform: translateY(24px);
}

// Move animation for stacking
.toast-slide-right-move,
.toast-slide-left-move,
.toast-slide-down-move,
.toast-slide-up-move {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (prefers-reduced-motion: reduce) {
  .toast-slide-right-enter-active,
  .toast-slide-right-leave-active,
  .toast-slide-left-enter-active,
  .toast-slide-left-leave-active,
  .toast-slide-down-enter-active,
  .toast-slide-down-leave-active,
  .toast-slide-up-enter-active,
  .toast-slide-up-leave-active {
    transition-duration: 0.01ms;
  }
}
</style>
